import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import PasscodeSetup from './PasscodeSetup';
import {
    getBiometricInfo,
    saveTokenInteractive,
    removeToken,
    getBiometricLabel,
    normalizeBiometricType,
    generateBiometricToken,
    subscribeBiometricManagerUpdated,
} from '../services/biometricService';

const apiUrl = process.env.REACT_APP_API_URL
    || (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'http://localhost:8000/api');
const PASSCODE_LENGTH = 6;

const Settings = ({ onBack }) => {
    const { user, setUser } = useUser();
    const [showPasscodeSetup, setShowPasscodeSetup] = useState(false);
    const [changePasscodeMode, setChangePasscodeMode] = useState(false);
    const [disableConfirm, setDisableConfirm] = useState(false);
    const [disableDigits, setDisableDigits] = useState('');
    const [disableError, setDisableError] = useState('');
    const [disableShake, setDisableShake] = useState(false);
    const [loading, setLoading] = useState(false);
    const [biometricInfo, setBiometricInfo] = useState({ available: false, type: null });
    const [biometricMode, setBiometricMode] = useState(user?.appLock?.biometricMode || 'any');
    const [biometricToggleDigits, setBiometricToggleDigits] = useState('');
    const [showBiometricPasscode, setShowBiometricPasscode] = useState(false);
    /** @type {'enable' | 'disable' | 'changeMode' | null} */
    const [biometricDialogAction, setBiometricDialogAction] = useState(null);
    const [biometricPasscodeError, setBiometricPasscodeError] = useState('');
    const [biometricPasscodeShake, setBiometricPasscodeShake] = useState(false);
    const [changeCurrentDigits, setChangeCurrentDigits] = useState('');
    const [showChangeCurrentPasscode, setShowChangeCurrentPasscode] = useState(false);
    const [changeCurrentError, setChangeCurrentError] = useState('');

    const appLockEnabled = user?.appLock?.enabled || false;
    const biometricEnabled = user?.appLock?.biometricEnabled || false;

    useEffect(() => {
        setBiometricMode(user?.appLock?.biometricMode || 'any');
    }, [user?.appLock?.biometricMode]);

    const refreshBiometricInfo = useCallback(async () => {
        const info = await getBiometricInfo();
        setBiometricInfo(info);
    }, []);

    useEffect(() => {
        let mounted = true;
        (async () => {
            if (!mounted) return;
            await refreshBiometricInfo();
        })();
        return () => { mounted = false; };
    }, [refreshBiometricInfo]);

    useEffect(() => {
        const unsub = subscribeBiometricManagerUpdated(() => {
            refreshBiometricInfo();
        });
        return unsub;
    }, [refreshBiometricInfo]);

    const handlePasscodeSetupComplete = useCallback(async ({ passcode, enableBiometric, biometricMode: modeFromSetup }) => {
        setLoading(true);
        try {
            let finalEnableBiometric = !!enableBiometric;
            const mode = modeFromSetup || biometricMode || 'any';
            let clientBiometricSecret = null;
            if (finalEnableBiometric) {
                clientBiometricSecret = generateBiometricToken(32);
                const saved = await saveTokenInteractive(clientBiometricSecret, 'Для включения биометрии');
                if (!saved) {
                    setBiometricPasscodeError('Не удалось сохранить ключ биометрии на устройстве. Используйте PIN.');
                    finalEnableBiometric = false;
                    clientBiometricSecret = null;
                }
            }
            const resp = await fetch(`${apiUrl}/front/app-lock/enable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: user.telegramId,
                    passcode,
                    enableBiometric: finalEnableBiometric,
                    biometricMode: mode,
                    biometricSecret: clientBiometricSecret,
                }),
            });
            const data = await resp.json();
            if (data.success) {
                setUser((prev) => ({
                    ...prev,
                    appLock: {
                        ...prev.appLock,
                        enabled: true,
                        biometricEnabled: finalEnableBiometric,
                        biometricMode: finalEnableBiometric ? mode : 'any',
                        failedAttempts: 0,
                        lockUntil: null,
                    },
                }));
                localStorage.setItem('appLockEnabled', 'true');
            }
        } catch (err) {
            console.error('[settings] enable error', err);
        } finally {
            setLoading(false);
            setShowPasscodeSetup(false);
        }
    }, [user, setUser, biometricMode]);

    const handleChangePasscodeComplete = useCallback(async ({ passcode }) => {
        setLoading(true);
        try {
            const resp = await fetch(`${apiUrl}/front/app-lock/change-passcode`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: user.telegramId,
                    currentPasscode: changeCurrentDigits,
                    newPasscode: passcode,
                }),
            });
            const data = await resp.json();
            if (!data.success && data.error) {
                console.error('[settings] change-passcode error', data.error);
            }
        } catch (err) {
            console.error('[settings] change-passcode error', err);
        } finally {
            setLoading(false);
            setChangePasscodeMode(false);
            setShowChangeCurrentPasscode(false);
            setChangeCurrentDigits('');
        }
    }, [user, changeCurrentDigits]);

    const handleDisable = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await fetch(`${apiUrl}/front/app-lock/disable`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: user.telegramId,
                    passcode: disableDigits,
                }),
            });
            const data = await resp.json();
            if (data.success) {
                await removeToken();
                setUser((prev) => ({
                    ...prev,
                    appLock: { enabled: false, biometricEnabled: false, biometricMode: 'any', failedAttempts: 0, lockUntil: null },
                }));
                localStorage.removeItem('appLockEnabled');
                setDisableConfirm(false);
                setDisableDigits('');
            } else {
                setDisableError(data.error || 'Неверный код-пароль');
                setDisableShake(true);
                setDisableDigits('');
                setTimeout(() => setDisableShake(false), 500);
            }
        } catch {
            setDisableError('Ошибка соединения');
        } finally {
            setLoading(false);
        }
    }, [user, disableDigits, setUser]);

    useEffect(() => {
        if (disableDigits.length === PASSCODE_LENGTH) handleDisable();
    }, [disableDigits, handleDisable]);

    const handleBiometricToggle = useCallback(async (passcodeOverride = null) => {
        setLoading(true);
        const newState = !biometricEnabled;
        const targetMode = biometricMode || 'any';
        const passcode = passcodeOverride ?? biometricToggleDigits;
        try {
            let clientBiometricSecret = null;
            if (newState) {
                clientBiometricSecret = generateBiometricToken(32);
                const label = getBiometricLabel(biometricInfo.type);
                const saved = await saveTokenInteractive(clientBiometricSecret, `Для использования ${label} в приложении`);
                if (!saved) {
                    setBiometricPasscodeError('Не удалось сохранить ключ биометрии на устройстве. Используйте PIN.');
                    setBiometricPasscodeShake(true);
                    setBiometricToggleDigits('');
                    setTimeout(() => setBiometricPasscodeShake(false), 500);
                    return;
                }
            }

            const resp = await fetch(`${apiUrl}/front/app-lock/biometric`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: user.telegramId,
                    passcode,
                    enable: newState,
                    mode: targetMode,
                    biometricSecret: clientBiometricSecret,
                }),
            });
            const data = await resp.json();
            if (data.success) {
                if (!newState) {
                    await removeToken();
                }
                setUser((prev) => ({
                    ...prev,
                    appLock: {
                        ...prev.appLock,
                        biometricEnabled: newState,
                        biometricMode: newState ? (data.biometricMode || targetMode) : 'any',
                    },
                }));
                setShowBiometricPasscode(false);
                setBiometricDialogAction(null);
                setBiometricToggleDigits('');
            } else {
                setBiometricPasscodeError(data.error || 'Неверный код-пароль');
                setBiometricPasscodeShake(true);
                setBiometricToggleDigits('');
                setTimeout(() => setBiometricPasscodeShake(false), 500);
            }
        } catch {
            setBiometricPasscodeError('Ошибка соединения');
        } finally {
            setLoading(false);
        }
    }, [user, biometricEnabled, biometricToggleDigits, setUser, biometricInfo.type, biometricMode]);

    const handleBiometricModeSave = useCallback(async (passcode, nextMode) => {
        setLoading(true);
        try {
            const resp = await fetch(`${apiUrl}/front/app-lock/biometric`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telegramId: user.telegramId,
                    passcode,
                    enable: true,
                    mode: nextMode,
                }),
            });
            const data = await resp.json();
            if (data.success) {
                setUser((prev) => ({
                    ...prev,
                    appLock: {
                        ...prev.appLock,
                        biometricEnabled: true,
                        biometricMode: data.biometricMode || nextMode,
                    },
                }));
                setShowBiometricPasscode(false);
                setBiometricDialogAction(null);
                setBiometricToggleDigits('');
            } else {
                setBiometricPasscodeError(data.error || 'Неверный код-пароль');
                setBiometricPasscodeShake(true);
                setBiometricToggleDigits('');
                setTimeout(() => setBiometricPasscodeShake(false), 500);
            }
        } catch {
            setBiometricPasscodeError('Ошибка соединения');
        } finally {
            setLoading(false);
        }
    }, [user, setUser]);

    useEffect(() => {
        if (changeCurrentDigits.length === PASSCODE_LENGTH && showChangeCurrentPasscode) {
            setChangePasscodeMode(true);
            setShowChangeCurrentPasscode(false);
        }
    }, [changeCurrentDigits, showChangeCurrentPasscode]);

    if (showPasscodeSetup) {
        return (
            <PasscodeSetup
                onComplete={handlePasscodeSetupComplete}
                onCancel={() => setShowPasscodeSetup(false)}
            />
        );
    }

    if (changePasscodeMode) {
        return (
            <PasscodeSetup
                isChangeMode
                onComplete={handleChangePasscodeComplete}
                onCancel={() => {
                    setChangePasscodeMode(false);
                    setChangeCurrentDigits('');
                }}
            />
        );
    }

    if (disableConfirm) {
        return renderPasscodeInput({
            title: 'Введите код-пароль для отключения',
            digits: disableDigits,
            setDigits: setDisableDigits,
            error: disableError,
            shake: disableShake,
            onCancel: () => { setDisableConfirm(false); setDisableDigits(''); setDisableError(''); },
            loading,
        });
    }

    if (showBiometricPasscode) {
        const action = biometricDialogAction || (biometricEnabled ? 'disable' : 'enable');
        const label = getBiometricLabel(biometricInfo.type);
        const detected = normalizeBiometricType(biometricInfo.type);
        const canPickFace = detected === 'unknown' || detected === 'face';
        const canPickTouch = detected === 'unknown' || detected === 'touch';
        const touchOptionLabel = getBiometricLabel('touch');
        const title = action === 'changeMode'
            ? 'Разрешённый способ'
            : action === 'disable'
                ? `Отключить ${label}`
                : `Включить ${label}`;
        const showModeSelect = action === 'enable' || action === 'changeMode';
        return renderPasscodeInput({
            title,
            digits: biometricToggleDigits,
            setDigits: setBiometricToggleDigits,
            error: biometricPasscodeError,
            shake: biometricPasscodeShake,
            onComplete: (code) => {
                if (action === 'changeMode') {
                    handleBiometricModeSave(code, biometricMode || 'any');
                } else {
                    handleBiometricToggle(code);
                }
            },
            onCancel: () => {
                setShowBiometricPasscode(false);
                setBiometricDialogAction(null);
                setBiometricToggleDigits('');
                setBiometricPasscodeError('');
            },
            loading,
            extraContent: showModeSelect ? (
                <div className="w-full max-w-[260px] mx-auto mb-4">
                    <div className="text-gray-400 text-xs mb-2">Разрешённый способ</div>
                    <select
                        className="w-full px-3 py-2 rounded-md bg-[#121212] border border-[#2a2a2a] text-white"
                        value={biometricMode || 'any'}
                        onChange={(e) => setBiometricMode(e.target.value)}
                    >
                        <option value="any">Любая биометрия</option>
                        <option value="face" disabled={!canPickFace}>Face ID</option>
                        <option value="touch" disabled={!canPickTouch}>{touchOptionLabel}</option>
                    </select>
                </div>
            ) : null,
        });
    }

    if (showChangeCurrentPasscode) {
        return renderPasscodeInput({
            title: 'Введите текущий код-пароль',
            digits: changeCurrentDigits,
            setDigits: setChangeCurrentDigits,
            error: changeCurrentError,
            shake: false,
            onCancel: () => { setShowChangeCurrentPasscode(false); setChangeCurrentDigits(''); setChangeCurrentError(''); },
            loading,
        });
    }

    const biometricLabel = getBiometricLabel(biometricInfo.type);
    const biometricAvailable = !!biometricInfo.available;
    const biometricModeSummary = (() => {
        const m = String(biometricMode || user?.appLock?.biometricMode || 'any').toLowerCase();
        if (m === 'face') return 'Face ID';
        if (m === 'touch') return getBiometricLabel('touch');
        return 'Любая биометрия';
    })();

    return (
        <div className="settings-screen">
            <div className="settings-header">
                <button className="back-button" onClick={onBack} style={{ position: 'relative' }}>
                    ◄ Назад
                </button>
                <h2 className="settings-title">Настройки</h2>
            </div>

            <div className="settings-section">
                <h3 className="settings-section-title">Безопасность</h3>

                <div className="settings-row" onClick={() => {
                    if (appLockEnabled) {
                        setDisableConfirm(true);
                    } else {
                        setShowPasscodeSetup(true);
                    }
                }}>
                    <div className="settings-row-left">
                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400" fill="currentColor">
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
                        </svg>
                        <span>Блокировка приложения</span>
                    </div>
                    <div className={`settings-toggle ${appLockEnabled ? 'settings-toggle-on' : ''}`}>
                        <div className="settings-toggle-thumb" />
                    </div>
                </div>

                {appLockEnabled && (
                    <div
                        className="settings-row"
                        onClick={biometricAvailable ? () => {
                            setBiometricDialogAction(biometricEnabled ? 'disable' : 'enable');
                            setBiometricPasscodeError('');
                            setBiometricToggleDigits('');
                            setShowBiometricPasscode(true);
                        } : undefined}
                        style={{ opacity: biometricAvailable ? 1 : 0.6 }}
                    >
                        <div className="settings-row-left">
                            <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400" fill="currentColor">
                                <path d="M9 11.75A1.25 1.25 0 1 0 9 9.25a1.25 1.25 0 0 0 0 2.5zm6 0a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-3 5.5c2.33 0 4.32-1.45 5.12-3.5h-1.67c-.69 1.19-1.97 2-3.45 2s-2.75-.81-3.45-2H6.88c.8 2.05 2.79 3.5 5.12 3.5zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                            </svg>
                            <span>{biometricLabel}</span>
                        </div>
                        {biometricAvailable ? (
                            <div className={`settings-toggle ${biometricEnabled ? 'settings-toggle-on' : ''}`}>
                                <div className="settings-toggle-thumb" />
                            </div>
                        ) : (
                            <span className="text-gray-400 text-sm">Недоступно</span>
                        )}
                    </div>
                )}

                {appLockEnabled && biometricAvailable && biometricEnabled && (
                    <div
                        className="settings-row"
                        onClick={() => {
                            setBiometricDialogAction('changeMode');
                            setBiometricPasscodeError('');
                            setBiometricToggleDigits('');
                            setShowBiometricPasscode(true);
                        }}
                    >
                        <div className="settings-row-left">
                            <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400" fill="currentColor">
                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                            <span>Разрешённый способ ({biometricModeSummary})</span>
                        </div>
                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                        </svg>
                    </div>
                )}

                {appLockEnabled && (
                    <div className="settings-row" onClick={() => setShowChangeCurrentPasscode(true)}>
                        <div className="settings-row-left">
                            <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-400" fill="currentColor">
                                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.488.488 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                            </svg>
                            <span>Изменить код-пароль</span>
                        </div>
                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-500" fill="currentColor">
                            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                        </svg>
                    </div>
                )}
            </div>
        </div>
    );
};

function renderPasscodeInput({ title, digits, setDigits, error, shake, onCancel, loading: isLoading, onComplete, extraContent = null }) {
    const handleDigit = (d) => {
        if (isLoading) return;
        if (digits.length >= PASSCODE_LENGTH) return;
        const next = `${digits}${d}`;
        setDigits(next);
        if (next.length === PASSCODE_LENGTH && typeof onComplete === 'function') {
            // Defer to the next frames so the WebView layout can settle before BiometricManager calls (Android).
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    onComplete(next);
                });
            });
        }
    };
    const handleBackspace = () => {
        if (isLoading) return;
        setDigits((prev) => prev.slice(0, -1));
    };

    return (
        <div className="lock-screen">
            <div className="lock-screen-content">
                <h2 className="lock-title">{title}</h2>
                {extraContent}
                <div className={`lock-dots ${shake ? 'lock-shake' : ''}`}>
                    {Array.from({ length: PASSCODE_LENGTH }).map((_, i) => (
                        <div key={i} className={`lock-dot ${i < digits.length ? 'lock-dot-filled' : ''}`} />
                    ))}
                </div>
                {error && <p className="lock-error">{error}</p>}
                <div className="lock-keypad">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                        <button key={d} className="lock-key" onClick={() => handleDigit(String(d))} disabled={isLoading}>
                            {d}
                        </button>
                    ))}
                    <button className="lock-key lock-key-action" onClick={onCancel}>
                        <span className="text-sm">Отмена</span>
                    </button>
                    <button className="lock-key" onClick={() => handleDigit('0')} disabled={isLoading}>
                        0
                    </button>
                    <button className="lock-key lock-key-action" onClick={handleBackspace} disabled={isLoading || digits.length === 0}>
                        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                            <path d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Settings;
